import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import StatCard from './StatCard';

interface ModelRow {
  model: string;
  location: string;
  kind: string;
  calls: number;
  ttft_p50_ms: number | null;
  ttft_p90_ms: number | null;
  total_p50_ms: number | null;
  total_p90_ms: number | null;
  retries: number;
}
interface ToolRow { name: string; calls: number; p50_ms: number; p90_ms: number }
export interface LatencyOverview {
  turns: number;
  timed_turns: number;
  turn_p50_ms: number | null;
  turn_p90_ms: number | null;
  ai_share: number | null;
  tool_share: number | null;
  models: ModelRow[];
  tools: ToolRow[];
}
interface ProbeTiming { status: number; ttft_ms: number | null; total_ms: number; error?: string }
interface ProbeRow { model: string; location: string; skipped?: string; short?: ProbeTiming; long?: ProbeTiming }

export function formatSeconds(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(Number(ms))) return '-';
  return `${(Number(ms) / 1000).toFixed(1)} s`;
}

const pct = (v: number | null) => (v === null ? '-' : `${Math.round(v * 100)}%`);

const probeCell = (t?: ProbeTiming) =>
  !t ? '-' : t.status !== 200 ? `failed (${t.status || t.error})` : `${formatSeconds(t.ttft_ms)} / ${formatSeconds(t.total_ms)}`;

const LatencyPanel: React.FC<{ from: Date; to: Date }> = ({ from, to }) => {
  const [data, setData] = useState<LatencyOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<ProbeRow[] | null>(null);
  const [probing, setProbing] = useState(false);
  const fromIso = from.toISOString();
  const toIso = to.toISOString();

  const load = useCallback(async () => {
    const { data: payload, error: err } = await supabase.rpc(
      'writer_latency_overview' as never,
      { p_from: fromIso, p_to: toIso } as never,
    );
    if (err) setError(err.message);
    else setData(payload as unknown as LatencyOverview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromIso.slice(0, 13)]);

  useEffect(() => {
    void load();
  }, [load]);

  const runProbe = async () => {
    setProbing(true);
    const { data: res, error: err } = await supabase.functions.invoke('writer-latency-probe', { body: {} });
    setProbing(false);
    if (err) setError(err.message);
    else setProbe((res as { results: ProbeRow[] }).results);
  };

  return (
    <section className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Where the time goes</h2>
          <p className="text-sm text-muted-foreground">
            Recorded for answers since timing was added ({data?.timed_turns ?? 0} of {data?.turns ?? 0} in range).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void runProbe()} disabled={probing}>
          {probing ? 'Testing the AI service…' : 'Test AI service speed'}
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Answer time, typical" value={formatSeconds(data?.turn_p50_ms)} />
        <StatCard label="Answer time, slowest 10%" value={formatSeconds(data?.turn_p90_ms)} />
        <StatCard label="Spent on the AI service" value={pct(data?.ai_share ?? null)} />
        <StatCard label="Spent on tools" value={pct(data?.tool_share ?? null)} />
      </div>

      {data && data.models.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-left">
              <tr>
                <th className="p-2">Model</th><th className="p-2">Region</th><th className="p-2">Call</th>
                <th className="p-2">Calls</th><th className="p-2">First output (typical / slow)</th>
                <th className="p-2">Total (typical / slow)</th><th className="p-2">Retries</th>
              </tr>
            </thead>
            <tbody>
              {data.models.map((m) => (
                <tr key={`${m.model}-${m.location}-${m.kind}`} className="border-t border-border/40">
                  <td className="p-2">{m.model}</td><td className="p-2">{m.location}</td><td className="p-2">{m.kind}</td>
                  <td className="p-2">{m.calls}</td>
                  <td className="p-2">{formatSeconds(m.ttft_p50_ms)} / {formatSeconds(m.ttft_p90_ms)}</td>
                  <td className="p-2">{formatSeconds(m.total_p50_ms)} / {formatSeconds(m.total_p90_ms)}</td>
                  <td className="p-2">{m.retries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.tools.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-left">
              <tr><th className="p-2">Tool</th><th className="p-2">Calls</th><th className="p-2">Typical</th><th className="p-2">Slowest 10%</th></tr>
            </thead>
            <tbody>
              {data.tools.map((t) => (
                <tr key={t.name} className="border-t border-border/40">
                  <td className="p-2">{t.name}</td><td className="p-2">{t.calls}</td>
                  <td className="p-2">{formatSeconds(t.p50_ms)}</td><td className="p-2">{formatSeconds(t.p90_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {probe && (
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-left">
              <tr><th className="p-2">Model</th><th className="p-2">Region</th><th className="p-2">Short request (first / total)</th><th className="p-2">Long request (first / total)</th></tr>
            </thead>
            <tbody>
              {probe.map((p) => (
                <tr key={`${p.model}-${p.location}`} className="border-t border-border/40">
                  <td className="p-2">{p.model}</td><td className="p-2">{p.location}</td>
                  <td className="p-2">{p.skipped ?? probeCell(p.short)}</td>
                  <td className="p-2">{p.skipped ? '' : probeCell(p.long)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default LatencyPanel;
